import { motion } from "framer-motion";
import { ArrowRight, AlertTriangle, ShieldCheck } from "lucide-react";
import { Link } from "react-router-dom";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import QuoteModal from "@/components/QuoteModal";
import usePageSEO from "@/hooks/usePageSEO";
import PageBreadcrumb from "@/components/PageBreadcrumb";
import PageFAQ from "@/components/PageFAQ";
import { staircaseFAQs } from "@/data/faqData";
import GoogleReviewsCarousel from "@/components/GoogleReviewsCarousel";

const staircaseHero = "/images/site/red-oak-staircase-dusky-walnut-floor-catwalk-view-farmington-hills-mi.webp";
import staircaseSpiralAerial from "@/assets/staircase-spiral-aerial.webp";
import staircaseSableBlack from "@/assets/staircase-sable-black-curved.webp";
import staircaseIronBalusters from "@/assets/staircase-iron-balusters.webp";
import staircaseFloatingWalnut from "@/assets/staircase-floating-walnut.webp";
import staircaseWhiteOakLanding from "@/assets/staircase-white-oak-landing.webp";
import staircaseDarkTreads from "@/assets/staircase-dark-treads-detail.webp";
import staircaseCarpetRunner from "@/assets/staircase-carpet-runner-oak.webp";

const fade = { initial: { opacity: 0, y: 28 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true } };

const threeTradeProcess = [
  {
    label: "The Floor Specialist",
    text: "We handle the surgical precision of dustless sanding and custom color matching. Ensuring your stairs and floors speak the same visual language.",
    num: "01",
  },
  {
    label: "The Stair Carpenter",
    text: "We coordinate with elite staircase specialists for the structural redesign of newel posts, handrails, and balusters. The architectural bones of your staircase.",
    num: "02",
  },
  {
    label: "The Fine-Finish Painter",
    text: "We manage the delicate order of operations, bringing painters in at the exact right moment for a factory-grade finish on risers and spindles. Timing is everything.",
    num: "03",
  },
];

const gallery = [
  { src: staircaseSpiralAerial, alt: "Farmington Hills spiral staircase portfolio — custom Red Oak color match to Dusky Walnut flooring — 1 Wood Floors Michigan", caption: "The Farmington Hills Spiral", description: "Red Oak to Hand Scraped Walnut Integration. Crafted by Northern Stairs, color-matched by 1 Wood Floors.", location: "Farmington Hills, Michigan", badge: "Custom Color Match Specialist" },
  { src: staircaseIronBalusters, alt: "Custom Hickory flooring matched with Sable Black and White high-contrast staircase in Northville, Michigan by 1 Wood Floors", caption: "The Northville Contrast", description: "Architectural Sable Black & Hickory Integration. Coordinated Three-Trade Symphony with CTM and Unique Painting.", location: "Northville, Michigan", badge: "High-Contrast Specialist" },
  { src: staircaseFloatingWalnut, alt: "Custom solid Walnut floating staircase and 8-inch wide-plank FAS Walnut flooring finished with Pallmann Magic Oil in Northville, Michigan by 1 Wood Floors", caption: "The Northville Floating Walnut", description: "Bespoke FAS Integration. Crafted by Brighton Stairs, finished with Pallmann Magic Oil 2K by 1 Wood Floors.", location: "Northville, Michigan", badge: "Floating Staircase Specialist" },
  { src: staircaseWhiteOakLanding, alt: "Custom color match of Boen European Oak Sand flooring to a minimalist modern staircase in Northville, Michigan by 1 Wood Floors", caption: "The Northville Euro-Minimalist", description: "Custom Boen Sand Integration. Restored by CTM, pastel pigment matched by 1 Wood Floors.", location: "Northville, Michigan", badge: "European Oak Specialist" },
  { src: staircaseDarkTreads, alt: "Custom Hickory Sparks flooring matched with a renovated staircase featuring a Dreamweaver Select carpet runner in Brighton, MI by 1 Wood Floors", caption: "The Brighton Hickory & Textile Integration", description: "Custom 'Sparks' Tonal Match  renovated by CTM, stained by 1 Wood Floors, Dreamweaver Select runner.", location: "Brighton, Michigan", badge: "Textile Integration Specialist" },
  { src: staircaseCarpetRunner, alt: "7-inch wide European Oak flooring and matching renovated staircase with Dreamweaver runner in Brighton, MI by 1 Wood Floors", caption: "The Brighton Whole-Home", description: "7-Inch European Oak & Dreamweaver Integration  renovated by CTM, finished by 1 Wood Floors.", location: "Brighton, Michigan", badge: "Whole-Home Integration Specialist" },
  
];

const StaircaseRenovations = () => {
  usePageSEO({
    title: "Custom Staircase Renovations | Carpentry and Finishing",
    description: "Custom hardwood staircase renovations and refinishing: color matched treads, balusters and architectural detailing by the owner, with Four NWFA Certifications.",
    path: "/staircase-renovations",
    // Service schema baked into static HTML via generate-seo-html.mjs
  });

  return (
    <article className="bg-background text-foreground">
      <SiteHeader />

      {/* ─── HERO ─── */}
      <section className="relative min-h-[70svh] max-h-[800px] flex items-end overflow-hidden bg-[hsl(var(--neutral-950))]">
        <PageBreadcrumb variant="overlay" items={[
          { label: "Home", to: "/" },
          { label: "Services" },
          { label: "Staircase Renovations" },
        ]} />
        <div className="absolute inset-0">
          <img width={1920} height={1080} loading="eager" decoding="async" fetchPriority="high"
            src={staircaseHero}
            alt="Curved staircase with color matched red oak treads over a hand scraped Dusky Walnut floor, Farmington Hills, MI"
            className="w-full h-full object-cover opacity-40"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-[hsl(var(--neutral-950))] via-[hsl(var(--neutral-950))]/60 to-transparent" />

        <motion.div {...fade} transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }} className="relative z-10 pb-12 md:pb-20 px-5 md:px-10 lg:px-16 max-w-3xl">
          <p className="text-[10px] md:text-[11px] font-semibold tracking-[.18em] uppercase text-[hsl(var(--gold-400))] mb-3">
            Specialty Service
          </p>
          <h1 className="font-display text-[clamp(32px,7vw,72px)] font-semibold leading-[1.05] tracking-tight text-primary-foreground">
            Staircase Refinishing and Custom Color Matching
          </h1>
          <p className="mt-4 md:mt-6 text-[13px] md:text-[15px] font-light text-primary-foreground/55 max-w-lg leading-relaxed">
            We don't just refinish stairs. We conduct a three-trade orchestra to deliver a result no single contractor can achieve alone.
          </p>
          <p className="mt-3 text-[11px] md:text-[12px] italic font-light text-[hsl(var(--gold-300))]/60 max-w-lg leading-relaxed">
            The Challenge: Matching a Red Oak staircase to a hand-scraped Dusky Walnut floor. The Solution: A proprietary 1 Wood Floors custom-color stain system that bridges the gap between different wood species for seamless architectural flow.
          </p>
        </motion.div>
      </section>

      {/* ─── THREE-TRADE PROCESS ─── */}
      <section className="bg-background py-16 md:py-24 lg:py-32">
        <div className="max-w-[1200px] mx-auto px-5 md:px-12">
          <motion.div {...fade} className="mb-10 md:mb-14">
            <p className="text-[11px] font-semibold tracking-[.18em] uppercase text-primary mb-2">The 1 Wood Standard</p>
            <h2 className="font-display text-[clamp(28px,5vw,48px)] font-semibold text-foreground leading-[1.1]">
              The Three-Trade <em className="italic font-normal text-primary">Process.</em>
            </h2>
            <p className="mt-4 text-[13px] md:text-[15px] font-light text-muted-foreground max-w-xl leading-relaxed">
              A staircase renovation isn't a one-man job. It's a precisely orchestrated sequence of three specialists. We serve as the conductor, ensuring every trade enters at the right moment.
            </p>
          </motion.div>
          <div className="grid md:grid-cols-3 gap-4">
            {threeTradeProcess.map((s, i) => (
              <motion.div key={s.label} {...fade} transition={{ delay: i * 0.06 }} className="border border-border/50 rounded-[20px] p-6 md:p-7 relative">
                <span className="absolute top-5 right-5 text-[40px] md:text-[48px] font-display font-bold text-border/30 leading-none select-none">{s.num}</span>
                <p className="text-[10px] font-bold tracking-[.16em] uppercase text-primary mb-3">{s.label}</p>
                <p className="text-[13px] md:text-[14px] font-light text-muted-foreground leading-relaxed">{s.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── SPECIES INTEGRATION ─── */}
      <section className="bg-[hsl(var(--neutral-950))] text-primary-foreground py-16 md:py-24 lg:py-32">
        <div className="max-w-[1200px] mx-auto px-5 md:px-12">
          <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center">
            <motion.div {...fade}>
              <div className="flex items-center gap-3 mb-4">
                <p className="text-[11px] font-semibold tracking-[.18em] uppercase text-[hsl(var(--gold-400))]">The Art of Species Integration</p>
                <span className="bg-[hsl(var(--gold-400))]/90 text-[hsl(var(--neutral-950))] text-[9px] font-bold tracking-[.14em] uppercase px-2.5 py-1 rounded-full">Custom Pigment Specialist</span>
              </div>
              <h2 className="font-display text-[clamp(28px,5vw,44px)] font-semibold leading-[1.1] mb-5">
                Red Oak to Amish <em className="italic font-normal text-[hsl(var(--gold-300))]">Hand-Scraped Walnut.</em>
              </h2>
              <p className="text-[10px] font-bold tracking-[.16em] uppercase text-[hsl(var(--gold-400))]/60 mb-4">The Challenge</p>
              <p className="text-[13px] md:text-[15px] font-light text-primary-foreground/60 leading-relaxed mb-5">
                We were commissioned to bridge the visual gap between a structural Red Oak staircase and a bespoke, <strong className="text-[hsl(var(--gold-400))] font-medium">Amish-crafted hand-scraped Walnut floor</strong>. The challenge? Red Oak naturally carries heavy pink and red undertones that clash with the deep, earthy pigments of Walnut.
              </p>
              <p className="text-[10px] font-bold tracking-[.16em] uppercase text-[hsl(var(--gold-400))]/60 mb-4">Our Solution</p>
              <p className="text-[13px] md:text-[15px] font-light text-primary-foreground/60 leading-relaxed mb-6">
                Rather than a costly tear-out, we developed a <strong className="text-[hsl(var(--gold-400))] font-medium">custom stain formula</strong>. By precisely balancing green and brown pigments to neutralize the Oak's natural warmth, we achieved a seamless tonal match  allowing the 120-year-old staircase to perfectly mirror the "Dusky" Walnut flooring and create a <strong className="text-[hsl(var(--gold-400))] font-medium">unified architectural flow</strong> throughout the home.
              </p>
              <Link
                to="/refinishing"
                className="inline-flex items-center gap-2 text-sm font-semibold text-[hsl(var(--gold-400))] hover:text-[hsl(var(--gold-300))] transition-colors"
              >
                Custom Color Matching
                <ArrowRight className="w-4 h-4" />
              </Link>
            </motion.div>
            <motion.div {...fade} transition={{ delay: 0.1 }} className="relative overflow-hidden rounded-[20px] aspect-[4/5]">
              <img width={800} height={534} loading="lazy"
                src={staircaseSpiralAerial}
                alt="Straight-down spiral view showing dual staircases — Farmington Hills MI home with custom Red Oak and Dusky Walnut species integration throughout — 1 Wood Floors"
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-[hsl(var(--neutral-950))]/80 to-transparent p-4 md:p-6 pt-12">
                <p className="text-[11px] md:text-[13px] font-light text-primary-foreground/70 leading-relaxed italic max-w-sm">
                  "This Farmington Hills home had multiple staircases — each requiring the same custom color match to the Dusky Walnut flooring throughout. Looking straight down the spiral you can see both staircases and the floor that connects them."
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ─── GEL STAIN WARNING ─── */}
      <section className="bg-background py-14 md:py-20">
        <div className="max-w-[1200px] mx-auto px-5 md:px-12">
          <motion.div {...fade} className="border border-destructive/20 bg-destructive/5 rounded-[20px] p-6 md:p-8 max-w-3xl">
            <div className="flex items-start gap-4">
              <AlertTriangle className="w-6 h-6 text-destructive shrink-0 mt-0.5" />
              <div>
                <h3 className="font-display text-lg md:text-xl font-semibold text-foreground mb-2">
                  The 'Gel Stain' Warning
                </h3>
                <p className="text-[13px] md:text-[14px] font-light text-muted-foreground leading-relaxed mb-4">
                  Your staircase is the most touched and highest-traffic surface in your home. Beware of "quick-fix" painting companies using <strong className="text-foreground font-medium">gel stains that peel and wear through</strong>. These coatings sit on top of the wood  they never penetrate the grain, and they won't survive daily use.
                </p>
                <div className="flex items-start gap-3 bg-background rounded-xl p-4 border border-border/50">
                  <ShieldCheck className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                  <p className="text-[13px] md:text-[14px] font-light text-muted-foreground leading-relaxed">
                    We provide true, <strong className="text-foreground font-medium">deep-penetrating professional finishes</strong> that are chemically bonded to the wood for a lifetime of durability.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ─── GALLERY ─── */}
      <section className="bg-[hsl(var(--neutral-950))] text-primary-foreground py-16 md:py-24 lg:py-32">
        <div className="max-w-[1200px] mx-auto px-5 md:px-12">
          <motion.div {...fade} className="mb-10 md:mb-14">
            <p className="text-[11px] font-semibold tracking-[.18em] uppercase text-[hsl(var(--gold-400))] mb-2">Featured Work</p>
            <h2 className="font-display text-[clamp(28px,5vw,48px)] font-semibold leading-[1.1]">
              Staircase <em className="italic font-normal text-[hsl(var(--gold-300))]">Portfolio.</em>
            </h2>
          </motion.div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
            {gallery.map((img, i) => (
              <motion.div key={i} {...fade} transition={{ delay: i * 0.06 }} className="relative overflow-hidden rounded-[20px] aspect-[4/5] group">
                <img width={800} height={534} loading="lazy"
                  src={img.src}
                  alt={img.alt}
                  className="absolute inset-0 w-full h-full object-cover transition-all duration-700 ease-out grayscale-[20%] contrast-[1.1] group-hover:grayscale-0 group-hover:scale-[1.04]"
                />
                {img.badge && (
                  <span className="absolute top-3 left-3 md:top-4 md:left-4 bg-[hsl(var(--gold-400))]/90 text-[hsl(var(--neutral-950))] text-[9px] md:text-[10px] font-bold tracking-[.14em] uppercase px-2.5 py-1 rounded-full backdrop-blur-sm">
                    {img.badge}
                  </span>
                )}
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-[hsl(var(--neutral-950))]/80 via-[hsl(var(--neutral-950))]/40 to-transparent p-4 pt-12 md:p-5 md:pt-16">
                  <h3 className="font-display text-sm md:text-base font-semibold text-primary-foreground leading-tight">{img.caption}</h3>
                  {img.description && (
                    <p className="text-[10px] md:text-[11px] font-light text-primary-foreground/50 mt-1 leading-relaxed max-w-xs">{img.description}</p>
                  )}
                  {img.location && (
                    <p className="text-[9px] md:text-[10px] tracking-[.12em] uppercase text-[hsl(var(--gold-400))]/70 mt-1">{img.location}</p>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── RELATED LINK ─── */}
      <section className="bg-background py-14 md:py-20">
        <div className="max-w-[1200px] mx-auto px-5 md:px-12">
          <motion.div {...fade} className="max-w-2xl">
            <p className="text-[13px] md:text-[15px] font-light text-muted-foreground leading-relaxed mb-5">
              See our staircase work in action. Three custom staircases restored during a high-stakes commercial project.
            </p>
            <Link
              to="/projects/ann-arbor-tech"
              className="inline-flex items-center gap-2 text-sm font-semibold text-primary hover:text-[hsl(var(--gold-400))] transition-colors"
            >
              View Ann Arbor Tech Case Study
              <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* ─── CTA ─── */}
      <section className="bg-[hsl(var(--gold-900))] py-14 md:py-20">
        <div className="max-w-[1200px] mx-auto px-5 md:px-12 text-center">
          <motion.div {...fade}>
            <h2 className="font-display text-[clamp(26px,5vw,44px)] font-semibold text-[hsl(var(--gold-100))] leading-[1.15] mb-4">
              Ready to transform<br className="hidden sm:block" /> your staircase?
            </h2>
            <p className="text-[13px] md:text-[15px] font-light text-[hsl(var(--gold-300))]/70 max-w-lg mx-auto leading-relaxed mb-7">
              Connect with Michigan's a team led by an NWFA Certified Installer and NWFA Certified Sand & Finisher for a $50 staircase estimate visit.
            </p>
            <QuoteModal
              trigger={
                <button className="bg-primary-foreground text-foreground px-7 py-3.5 rounded-full text-sm font-medium hover:bg-[hsl(var(--gold-100))] transition-colors">
                  Schedule a Consultation
                </button>
              }
            />
          </motion.div>
        </div>
      </section>

      <GoogleReviewsCarousel contextTags={["staircase-renovations", "installation"]} count={4} />
      <PageFAQ suppressJsonLd faqs={staircaseFAQs} heading={<>Staircase questions <em className="italic font-normal text-primary">answered.</em></>} subheading="Staircase FAQs" />
      <SiteFooter />
    </article>
  );
};

export default StaircaseRenovations;
